import {
  humanizePersonaCompetitor,
  humanizePersonaSentence,
  humanizePersonaTitle,
  personaInitials,
} from "@/features/personas/lib/persona-format";
import type { TFunction } from "i18next";

import { PERSONA_OPPORTUNITY_DEFAULT_STICK_DAYS } from "../constants/persona-opportunity-tuning-defaults";
import type {
  PersonaOpportunityRow,
  PersonaOpportunitySetDetail,
  PersonaOpportunityStatus,
} from "../api/persona-opportunity-sets.api";

/**
 * Opportunity-level view model (KTD9) — one shared shape for Cards + Table.
 * Built from `PersonaOpportunitySetDetail`; never invents Strength / drivingFeatureScore.
 */

export type PersonaOpportunityDrivenBy = "painpoint" | "motivation";

export interface PersonaOpportunityDisplayRow {
  id: string;
  personaId: string;
  status: PersonaOpportunityStatus;
  angle: string;
  painPoint: string | null;
  motivation: string | null;
  /** Which feature type scored this angle — drives Why fallback + UI highlight. */
  drivenBy: PersonaOpportunityDrivenBy | null;
  /** v5 Why sentence built from investment + competitor + stick KPIs. */
  why: string;
  creativesCount: number;
  competitorsRunningCount: number;
  competitorsTotal: number;
  stickRatePct: number;
  duplicatedPct: number;
  myInvestmentSharePct: number;
  marketInvestmentSharePct: number;
  /** Null when the backend omitted the multiple — UI shows an em dash. */
  investmentIndex: number | null;
  thumbnailUrl: string | null;
  /** Supporting-ad creative URL (video / media). */
  mediaUrl: string | null;
  daysLive: number | null;
  /** Supporting-ad duplication count from generation snapshot. */
  duplications: number | null;
  /** Supporting-ad brand slug humanized for display (`first_aid_beauty` → `First Aid Beauty`). */
  competitorName: string | null;
  /** First letter of the brand for the avatar fallback when no logo URL is stored. */
  competitorInitial: string | null;
  /** Competitor logo from BQ `brands_metadata.profile_picture_url` when present. */
  competitorProfilePictureUrl: string | null;
  rank: number;
}

export interface PersonaOpportunityPersonaGroup {
  personaId: string;
  title: string;
  who: string | null;
  initials: string;
  pains: string[];
  motivations: string[];
  /** True once BigQuery stopped returning this persona — the group renders with a badge. */
  isStale: boolean;
  opportunities: PersonaOpportunityDisplayRow[];
}

/** Opportunities-tab model — all opportunity rows for the set (every status). */
export interface PersonaOpportunityOpportunitiesModel {
  groups: PersonaOpportunityPersonaGroup[];
  personaCount: number;
  angleCount: number;
  competitorsTotal: number;
}

/** @deprecated Use `PersonaOpportunityOpportunitiesModel`. */
export type PersonaOpportunityActiveModel = PersonaOpportunityOpportunitiesModel;

/**
 * Design Why line:
 * "The market invests 15× more here than you do — 7 of 12 competitors run it,
 * and 68% of those ads stick past 14 days."
 * When investment index is missing, lead with a no-investment sentence keyed off `drivenBy`.
 */
export function buildPersonaOpportunityWhyLine(
  input: {
    investmentIndex: number | null;
    competitorsRunningCount: number;
    competitorsTotal: number;
    stickRatePct: number;
    stickDays: number;
    drivenBy: PersonaOpportunityDrivenBy | null;
  },
  t: TFunction,
): string {
  const competitors = formatCompetitorsOfTotal(
    input.competitorsRunningCount,
    input.competitorsTotal,
  );
  const stickRate = formatPercent(input.stickRatePct);
  const trailing = t("personaOpportunities.active.why.trailing", {
    competitors,
    stickRate,
    stickDays: input.stickDays,
    defaultValue:
      "{{competitors}} competitors run it, and {{stickRate}} of those ads stick past {{stickDays}} days.",
  });

  const multiple = formatInvestmentMultiple(input.investmentIndex);
  if (multiple) {
    return t("personaOpportunities.active.why.withIndex", {
      multiple,
      trailing,
      defaultValue: "The market invests {{multiple}} here than you do — {{trailing}}",
    });
  }

  if (input.drivenBy === "motivation") {
    return t("personaOpportunities.active.why.noInvestmentMotivation", {
      trailing,
      defaultValue: "You don't invest in this motivation at all — {{trailing}}",
    });
  }

  if (input.drivenBy === "painpoint") {
    return t("personaOpportunities.active.why.noInvestmentPainPoint", {
      trailing,
      defaultValue: "You don't invest in this pain point at all — {{trailing}}",
    });
  }

  return t("personaOpportunities.active.why.noInvestmentAngle", {
    trailing,
    defaultValue: "You don't invest in this angle at all — {{trailing}}",
  });
}

function toDisplayRow(
  opportunity: PersonaOpportunityRow,
  competitorsTotal: number,
  stickDays: number,
  t: TFunction,
): PersonaOpportunityDisplayRow {
  const { kpis, supportingAd } = opportunity;
  const competitorRaw = supportingAd?.competitor?.trim() || null;
  const competitorName = competitorRaw ? humanizePersonaCompetitor(competitorRaw) : null;
  const drivenBy = opportunity.drivenBy;
  const investmentIndex = kpis.investmentIndex ?? null;

  return {
    id: opportunity.id,
    personaId: opportunity.personaId,
    status: opportunity.status,
    angle: humanizePersonaTitle(opportunity.angle),
    painPoint: opportunity.painPoint ? humanizePersonaSentence(opportunity.painPoint) : null,
    motivation: opportunity.motivation ? humanizePersonaSentence(opportunity.motivation) : null,
    drivenBy,
    why: buildPersonaOpportunityWhyLine(
      {
        investmentIndex,
        competitorsRunningCount: kpis.competitorsRunningCount,
        competitorsTotal,
        stickRatePct: kpis.stickRatePct,
        stickDays,
        drivenBy,
      },
      t,
    ),
    creativesCount: kpis.creativesCount,
    competitorsRunningCount: kpis.competitorsRunningCount,
    competitorsTotal,
    stickRatePct: kpis.stickRatePct,
    duplicatedPct: kpis.duplicatedPct,
    myInvestmentSharePct: kpis.myInvestmentSharePct,
    marketInvestmentSharePct: kpis.marketInvestmentSharePct,
    investmentIndex,
    thumbnailUrl: supportingAd?.thumbnailUrl ?? null,
    mediaUrl: supportingAd?.url ?? null,
    daysLive: supportingAd?.daysLive ?? null,
    duplications: supportingAd?.duplications ?? null,
    competitorName,
    competitorInitial: competitorName ? competitorName.charAt(0).toUpperCase() : null,
    competitorProfilePictureUrl: supportingAd?.profilePictureUrl?.trim() || null,
    rank: opportunity.rank,
  };
}

/**
 * Maps set detail → persona groups for the Opportunities tab.
 * Includes **all** opportunity statuses; sort within group is applied by the filter layer.
 */
export function toPersonaOpportunityOpportunitiesModel(
  detail: PersonaOpportunitySetDetail,
  t: TFunction,
): PersonaOpportunityOpportunitiesModel {
  const competitorsTotal = detail.set.competitorsTotal;
  const stickDays = detail.set.config?.stickDays ?? PERSONA_OPPORTUNITY_DEFAULT_STICK_DAYS;
  const groups: PersonaOpportunityPersonaGroup[] = detail.personas.map((section) => {
    const title = humanizePersonaTitle(section.title);
    const opportunities = [...section.opportunities]
      .filter((opportunity) => opportunity.isCustom !== true)
      .sort((a, b) => a.rank - b.rank)
      .map((opportunity) => toDisplayRow(opportunity, competitorsTotal, stickDays, t));

    return {
      personaId: section.personaId,
      title,
      who: section.who ? humanizePersonaSentence(section.who) : null,
      initials: personaInitials(title),
      pains: section.pains.map(humanizePersonaSentence),
      motivations: section.motivations.map(humanizePersonaSentence),
      isStale: section.staledAt !== null,
      opportunities,
    };
  });

  groups.sort((a, b) => a.title.localeCompare(b.title));

  const angleCount = groups.reduce((total, group) => total + group.opportunities.length, 0);

  return {
    groups,
    personaCount: groups.length,
    angleCount,
    competitorsTotal,
  };
}

/** @deprecated Use `toPersonaOpportunityOpportunitiesModel`. */
export const toPersonaOpportunityActiveModel = toPersonaOpportunityOpportunitiesModel;

/**
 * KPI rates from the persona-angles pipeline are fractional (0–1).
 * Multiply by 100 only at display (see PersonaAngleRowSchema).
 */
export function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

export function formatCompetitorsOfTotal(running: number, total: number): string {
  return `${running} of ${total}`;
}

/** e.g. `15× more` — returns null when index is missing so callers can show "—". */
export function formatInvestmentMultiple(index: number | null | undefined): string | null {
  if (index == null || !Number.isFinite(index)) return null;
  const rounded = Number.isInteger(index) ? String(index) : index.toFixed(1).replace(/\.0$/, "");
  return `${rounded}× more`;
}
